import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from '../utils/axiosConfig';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import {
    FaServer, FaLaptop, FaMobileAlt, FaTabletAlt, FaPrint,
    FaDesktop, FaLink, FaMagic, FaSyncAlt, FaChevronDown, FaChevronUp,
    FaExclamationCircle, FaClock, FaMapMarkerAlt
} from 'react-icons/fa';
import Modal from './common/Modal';
import './EventosView.css';

const ACTIVO_TIPOS = [
    { value: 'asset', label: 'Equipo', icon: FaDesktop },
    { value: 'smartphone', label: 'Smartphone', icon: FaMobileAlt },
    { value: 'tablet', label: 'Tablet', icon: FaTabletAlt },
    { value: 'impresora', label: 'Impresora', icon: FaPrint },
    { value: 'host', label: 'Servidor', icon: FaServer },
    { value: 'vm', label: 'Máquina Virtual', icon: FaLaptop },
];

const SEV_COLORS = {
    baja: { bg: '#ECFDF5', text: '#059669', bar: '#10B981' },
    media: { bg: '#FFFBEB', text: '#D97706', bar: '#F59E0B' },
    alta: { bg: '#FEF2F2', text: '#DC2626', bar: '#EF4444' },
    critica: { bg: '#450A0A', text: '#FCA5A5', bar: '#7F1D1D' },
};

const ESTADO_STYLES = {
    abierto: 'bg-gray-100 text-gray-600',
    en_proceso: 'bg-blue-50 text-blue-600',
    resuelto: 'bg-green-50 text-green-600',
    cerrado: 'bg-gray-50 text-gray-400',
};

const parseSede = (sedesStr) => {
    try {
        const parsed = JSON.parse(sedesStr);
        return Array.isArray(parsed) ? parsed[0] : parsed;
    } catch {
        return sedesStr || '';
    }
};

const EventosView = ({ showCreateModal, onCloseCreateModal, onEventCreated }) => {
    const [eventos, setEventos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewingEvento, setViewingEvento] = useState(null);

    const [filtros, setFiltros] = useState({ categoria_id: '', dias: 7 });

    const [formData, setFormData] = useState({
        categoria_id: '', descripcion: '', sede: '',
        tipo: 'incidente', severidad: 'media',
        referencia_tipo: '', referencia_id: ''
    });
    const [activos, setActivos] = useState([]);
    const [activoSearch, setActivoSearch] = useState('');

    useEffect(() => {
        cargarDatos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtros]);

    const cargarDatos = async () => {
        try {
            setLoading(true);
            const [categoriasRes, timelineRes] = await Promise.all([
                axios.get('/api/eventos/categorias'),
                axios.get('/api/eventos/timeline', {
                    params: { dias: filtros.dias, categoria_id: filtros.categoria_id }
                })
            ]);
            setCategorias(categoriasRes.data);
            const arr = [];
            if (timelineRes.data.eventos_por_fecha) {
                Object.values(timelineRes.data.eventos_por_fecha).forEach(d => arr.push(...d));
            }
            arr.sort((a, b) => new Date(b.fecha_inicio) - new Date(a.fecha_inicio));
            setEventos(arr);
        } catch (error) {
            console.error('Error al cargar datos:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!formData.referencia_tipo) { setActivos([]); return; }
        const timer = setTimeout(async () => {
            try {
                const res = await axios.get('/api/eventos/activos', {
                    params: { tipo: formData.referencia_tipo, search: activoSearch }
                });
                setActivos(res.data);
            } catch (err) { console.error(err); }
        }, 300);
        return () => clearTimeout(timer);
    }, [formData.referencia_tipo, activoSearch]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const cat = categorias.find(c => c.id === parseInt(formData.categoria_id));
            const tipoC = formData.tipo.charAt(0).toUpperCase() + formData.tipo.slice(1);
            await axios.post('/api/eventos/', {
                ...formData,
                titulo: `${tipoC} en ${formData.sede} - ${cat?.nombre || 'General'}`,
                referencia_id: formData.referencia_id || null,
                referencia_tipo: formData.referencia_tipo || null
            });
            onCloseCreateModal();
            resetForm();
            cargarDatos();
            onEventCreated?.();
        } catch (error) {
            console.error('Error al crear evento:', error);
        }
    };

    const resetForm = () => {
        setFormData({ categoria_id: '', descripcion: '', sede: '', tipo: 'incidente', severidad: 'media', referencia_tipo: '', referencia_id: '' });
        setActivoSearch('');
        setActivos([]);
    };

    // Count by severity for the header
    const sevCounts = eventos.reduce((acc, e) => {
        acc[e.severidad] = (acc[e.severidad] || 0) + 1;
        return acc;
    }, {});

    return (
        <>
            {/* Panel header with filters + severity summary */}
            <div className="ops-panel-header" style={{ flexWrap: 'wrap' }}>
                <div className="flex items-center gap-3">
                    <h2 className="ops-panel-title">Bitacora</h2>
                    {/* Inline severity counters */}
                    <div className="flex items-center gap-1">
                        {['critica', 'alta', 'media', 'baja'].map(sev => {
                            const count = sevCounts[sev] || 0;
                            if (count === 0) return null;
                            const colors = SEV_COLORS[sev];
                            return (
                                <span
                                    key={sev}
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold"
                                    style={{ backgroundColor: colors.bg, color: colors.text }}
                                >
                                    {count}
                                </span>
                            );
                        })}
                    </div>
                </div>
                <div className="ev-filters">
                    <select
                        value={filtros.dias}
                        onChange={(e) => setFiltros({ ...filtros, dias: e.target.value })}
                    >
                        <option value="7">7d</option>
                        <option value="15">15d</option>
                        <option value="30">30d</option>
                        <option value="90">90d</option>
                    </select>
                    <select
                        value={filtros.categoria_id}
                        onChange={(e) => setFiltros({ ...filtros, categoria_id: e.target.value })}
                    >
                        <option value="">Todas</option>
                        {categorias.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Event feed */}
            <div className="ops-panel-body">
                {loading ? (
                    <div className="ev-loading">
                        <div className="w-6 h-6 border-2 border-gray-200 border-t-sura-blue rounded-full animate-spin" />
                    </div>
                ) : eventos.length === 0 ? (
                    <div className="ev-empty">
                        <FaExclamationCircle size={24} />
                        <p>Sin eventos en este periodo</p>
                    </div>
                ) : (
                    <div className="ev-feed">
                        {eventos.map((evento) => {
                            const fecha = new Date(evento.fecha_inicio);
                            const sede = parseSede(evento.sedes_afectadas);
                            const sev = SEV_COLORS[evento.severidad] || SEV_COLORS.media;

                            return (
                                <div
                                    key={evento.id}
                                    className="ev-row"
                                    onClick={() => setViewingEvento(evento)}
                                >
                                    {/* Severity bar */}
                                    <div
                                        className="ev-sev-bar"
                                        style={{ backgroundColor: sev.bar }}
                                    />

                                    {/* Content */}
                                    <div className="ev-content">
                                        <div className="ev-title-row">
                                            <span className="ev-title">{evento.titulo}</span>
                                            <span
                                                className="ev-sev-badge"
                                                style={{ backgroundColor: sev.bg, color: sev.text }}
                                            >
                                                {evento.severidad}
                                            </span>
                                        </div>
                                        <div className="ev-meta">
                                            <span className="flex items-center gap-1">
                                                <FaClock size={9} />
                                                {formatDistanceToNow(fecha, { addSuffix: true, locale: es })}
                                            </span>
                                            {sede && (
                                                <span className="ev-meta-sep flex items-center gap-1">
                                                    <FaMapMarkerAlt size={8} />
                                                    {sede}
                                                </span>
                                            )}
                                            {evento.categoria?.nombre && (
                                                <span className="ev-meta-sep">
                                                    {evento.categoria.nombre}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Estado */}
                                    <span className={`ev-estado ${ESTADO_STYLES[evento.estado] || ''}`}>
                                        {evento.estado === 'en_proceso' ? 'en proceso' : evento.estado}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Modal: Crear Evento */}
            <Modal isOpen={showCreateModal} onClose={onCloseCreateModal} size="md">
                <Modal.Header onClose={onCloseCreateModal}>
                    Registrar Evento
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={handleSubmit} className="ev-form">
                        <div className="form-row">
                            <div className="form-group">
                                <label>Categoría</label>
                                <select value={formData.categoria_id} onChange={(e) => setFormData({ ...formData, categoria_id: e.target.value })} required>
                                    <option value="">Seleccionar</option>
                                    {categorias.map(cat => <option key={cat.id} value={cat.id}>{cat.nombre}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Sede</label>
                                <select value={formData.sede} onChange={(e) => setFormData({ ...formData, sede: e.target.value })} required>
                                    <option value="">Seleccionar</option>
                                    <option value="CA1">CA1</option>
                                    <option value="CDN">CDN</option>
                                    <option value="CCS">CCS</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Descripción</label>
                            <textarea value={formData.descripcion} onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })} rows="3" placeholder="Detalles del evento..." />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Tipo</label>
                                <select value={formData.tipo} onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}>
                                    <option value="incidente">Incidente</option>
                                    <option value="mantenimiento">Mantenimiento</option>
                                    <option value="alerta">Alerta</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Severidad</label>
                                <select value={formData.severidad} onChange={(e) => setFormData({ ...formData, severidad: e.target.value })}>
                                    <option value="baja">Baja</option>
                                    <option value="media">Media</option>
                                    <option value="alta">Alta</option>
                                    <option value="critica">Crítica</option>
                                </select>
                            </div>
                        </div>

                        {/* Vincular activo */}
                        <div className="form-group" style={{ borderTop: '1px solid #f1f5f9', paddingTop: '12px' }}>
                            <label className="flex items-center gap-1.5">
                                <FaLink size={10} className="text-gray-400" />
                                Vincular a activo
                            </label>
                            <div className="form-row">
                                <select
                                    value={formData.referencia_tipo}
                                    onChange={(e) => { setFormData({ ...formData, referencia_tipo: e.target.value, referencia_id: '' }); setActivoSearch(''); }}
                                >
                                    <option value="">Sin vincular</option>
                                    {ACTIVO_TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                                {formData.referencia_tipo && (
                                    <input type="text" placeholder="Buscar..." value={activoSearch} onChange={(e) => setActivoSearch(e.target.value)} />
                                )}
                            </div>
                            {activos.length > 0 && formData.referencia_tipo && (
                                <div className="mt-2 max-h-28 overflow-y-auto border border-gray-200 rounded-lg">
                                    {activos.map(a => (
                                        <button key={a.id} type="button"
                                            onClick={() => { setFormData({ ...formData, referencia_id: a.id }); setActivoSearch(a.nombre); setActivos([]); }}
                                            className={`w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 flex justify-between ${formData.referencia_id === a.id ? 'bg-blue-50 text-blue-700' : 'text-gray-600'}`}
                                        >
                                            <span className="font-medium">{a.nombre}</span>
                                            <span className="text-gray-400">{a.detalle}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="form-actions">
                            <button type="button" onClick={onCloseCreateModal} className="px-4 py-2 text-sm font-semibold text-gray-500 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                                Cancelar
                            </button>
                            <button type="submit" className="px-4 py-2 text-sm font-semibold text-white bg-sura-blue rounded-lg hover:bg-sura-blue-dark transition-colors">
                                Guardar
                            </button>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>

            {/* Modal: Detalle */}
            {viewingEvento && (
                <EventoDetailModal
                    evento={viewingEvento}
                    onClose={() => setViewingEvento(null)}
                    onUpdated={cargarDatos}
                />
            )}
        </>
    );
};


// ---- Detail Modal ----
const EventoDetailModal = ({ evento, onClose, onUpdated }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showPostmortem, setShowPostmortem] = useState(false);
    const [generatingPM, setGeneratingPM] = useState(false);
    const [closingEvento, setClosingEvento] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`/api/eventos/${evento.id}`);
                setData(res.data);
            } catch (err) { console.error(err); }
            finally { setLoading(false); }
        })();
    }, [evento.id]);

    const generatePostmortem = async () => {
        setGeneratingPM(true);
        try {
            const res = await axios.post(`/api/eventos/${evento.id}/postmortem`);
            setData(prev => ({ ...prev, postmortem: res.data.postmortem }));
            setShowPostmortem(true);
        } catch (err) { console.error(err); }
        finally { setGeneratingPM(false); }
    };

    const handleCerrarEvento = async () => {
        setClosingEvento(true);
        try {
            await axios.put(`/api/eventos/${evento.id}`, {
                estado: 'cerrado',
                fecha_fin: new Date().toISOString()
            });
            setData(prev => ({ ...prev, estado: 'cerrado', fecha_fin: new Date().toISOString() }));
            onUpdated?.();
        } catch (err) { console.error(err); }
        finally { setClosingEvento(false); }
    };

    const sev = SEV_COLORS[data?.severidad] || SEV_COLORS.media;
    const activoTipo = ACTIVO_TIPOS.find(t => t.value === data?.referencia_tipo);
    const isCritical = data?.severidad === 'critica';
    const isClosed = data?.estado === 'resuelto' || data?.estado === 'cerrado';
    const sede = parseSede(data?.sedes_afectadas);

    return (
        <Modal isOpen={true} onClose={onClose} size="lg">
            <Modal.Header onClose={onClose}>
                Detalle del Evento
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="ev-loading">
                        <div className="w-6 h-6 border-2 border-gray-200 border-t-sura-blue rounded-full animate-spin" />
                    </div>
                ) : data ? (
                    <div className="space-y-4">
                        {/* Header */}
                        <div className="flex items-start gap-3">
                            <div className="w-1.5 self-stretch rounded-full flex-shrink-0" style={{ backgroundColor: sev.bar, minHeight: 40 }} />
                            <div className="flex-1">
                                <h3 className="text-base font-bold text-gray-900 mb-1.5">{data.titulo}</h3>
                                <div className="flex flex-wrap items-center gap-1.5">
                                    <span className="ev-sev-badge" style={{ backgroundColor: sev.bg, color: sev.text }}>
                                        {data.severidad}
                                    </span>
                                    <span className={`ev-estado ${ESTADO_STYLES[data.estado] || ''}`}>
                                        {data.estado}
                                    </span>
                                    {sede && (
                                        <span className="text-[10px] font-semibold text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded uppercase">
                                            {sede}
                                        </span>
                                    )}
                                    {data.categoria?.nombre && (
                                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: `${data.categoria.color}15`, color: data.categoria.color }}>
                                            {data.categoria.nombre}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Descripción */}
                        {data.descripcion && (
                            <p className="text-sm text-gray-600 whitespace-pre-wrap bg-gray-50 rounded-lg p-3 border border-gray-100">
                                {data.descripcion}
                            </p>
                        )}

                        {/* Info grid */}
                        <div className="ev-detail-grid">
                            <div className="ev-detail-cell">
                                <div className="label">Inicio</div>
                                <div className="value">{data.fecha_inicio ? format(new Date(data.fecha_inicio), "dd/MM/yyyy HH:mm", { locale: es }) : 'N/A'}</div>
                            </div>
                            <div className="ev-detail-cell">
                                <div className="label">Fin</div>
                                <div className="value">{data.fecha_fin ? format(new Date(data.fecha_fin), "dd/MM/yyyy HH:mm", { locale: es }) : 'En curso'}</div>
                            </div>
                            {data.duracion_minutos && (
                                <div className="ev-detail-cell">
                                    <div className="label">Duración</div>
                                    <div className="value">{data.duracion_minutos} min</div>
                                </div>
                            )}
                            {data.reportado_por && (
                                <div className="ev-detail-cell">
                                    <div className="label">Reportado por</div>
                                    <div className="value">{data.reportado_por}</div>
                                </div>
                            )}
                        </div>

                        {/* Activo vinculado */}
                        {activoTipo && data.referencia_id && (
                            <div className="flex items-center gap-2 p-2.5 bg-indigo-50 rounded-lg border border-indigo-100 text-sm">
                                <activoTipo.icon size={12} className="text-indigo-500" />
                                <span className="font-semibold text-indigo-700 text-xs">{activoTipo.label} #{data.referencia_id}</span>
                            </div>
                        )}

                        {/* Solución */}
                        {data.solucion && (
                            <div className="bg-green-50 rounded-lg border border-green-100 p-3">
                                <div className="text-[10px] font-bold text-green-600 uppercase tracking-wider mb-1">Solución</div>
                                <p className="text-sm text-green-800 whitespace-pre-wrap">{data.solucion}</p>
                            </div>
                        )}

                        {/* Post-mortem */}
                        {(isCritical || data.postmortem) && (
                            <div className="rounded-lg border border-gray-200 overflow-hidden">
                                <button
                                    onClick={() => setShowPostmortem(!showPostmortem)}
                                    className="w-full px-3 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between hover:bg-gray-100 transition-colors"
                                >
                                    <span className="text-xs font-bold text-gray-600 flex items-center gap-1.5 uppercase tracking-wider">
                                        <FaMagic size={10} className="text-purple-500" />
                                        Post-mortem IA
                                    </span>
                                    {showPostmortem ? <FaChevronUp size={10} className="text-gray-400" /> : <FaChevronDown size={10} className="text-gray-400" />}
                                </button>
                                <AnimatePresence>
                                    {showPostmortem && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.15 }}
                                            className="overflow-hidden"
                                        >
                                            <div className="p-4">
                                                {data.postmortem ? (
                                                    <div className="prose prose-sm max-w-none text-gray-700 [&_h1]:text-sm [&_h2]:text-sm [&_h3]:text-xs [&_p]:text-sm [&_li]:text-sm [&_strong]:text-gray-900">
                                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.postmortem}</ReactMarkdown>
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-4">
                                                        <p className="text-xs text-gray-500 mb-3">
                                                            {isClosed
                                                                ? 'Genera un análisis post-mortem de este incidente.'
                                                                : 'Disponible al resolver o cerrar el evento.'}
                                                        </p>
                                                        {isClosed && (
                                                            <button
                                                                onClick={generatePostmortem}
                                                                disabled={generatingPM}
                                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg text-xs font-semibold hover:bg-purple-100 transition-colors disabled:opacity-50"
                                                            >
                                                                {generatingPM
                                                                    ? <><FaSyncAlt size={10} className="animate-spin" /> Generando...</>
                                                                    : <><FaMagic size={10} /> Generar Post-mortem</>
                                                                }
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        )}
                    </div>
                ) : (
                    <p className="text-center text-gray-400 py-8 text-sm">Error cargando datos</p>
                )}
            </Modal.Body>
            <Modal.Footer>
                <div className="flex items-center justify-between w-full">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-gray-500 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                        Cerrar
                    </button>
                    {!isClosed && data && (
                        <button
                            type="button"
                            onClick={handleCerrarEvento}
                            disabled={closingEvento}
                            className="px-4 py-2 text-sm font-semibold text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                        >
                            {closingEvento ? 'Cerrando...' : 'Cerrar Evento'}
                        </button>
                    )}
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default EventosView;
